import { useCallback, useMemo } from "react";

import { useFormikContext } from "formik";

import { Checkbox } from "components/form/elements";

import { TEXTS } from "../constants";
import { TiVoTargetsListProps, TiVoTargetsForm } from "../types";
import { useTiVoDeviceData } from "../useTiVoDeviceData";
import { getSelectedPlatformTypes } from "../utils";
import styles from "./TargetSearchList.scss";

export function TargetSummary({
  listName,
  items,
  restrictTargets,
  dataTestId = "target-summary",
}: TiVoTargetsListProps) {
  const { values, setValues } = useFormikContext<TiVoTargetsForm>();
  const { getClientTypeIds, getPlatformType } = useTiVoDeviceData();

  const platformTypes = useMemo(
    () => getSelectedPlatformTypes(values, getPlatformType),
    [values, getPlatformType]
  );

  const filterByPlatformTypes = useCallback(
    (deviceIds: string[]) =>
      deviceIds.filter(
        (deviceId) =>
          platformTypes.length === 0 ||
          platformTypes.includes(getPlatformType(deviceId))
      ),
    [platformTypes, getPlatformType]
  );

  const listGroups = useMemo(
    () => filterByPlatformTypes(Object.keys(items)),
    [items, filterByPlatformTypes]
  );

  const listOptions = useMemo(
    () => filterByPlatformTypes(Object.values(items).flat()),
    [items, filterByPlatformTypes]
  );

  const value = useMemo(() => values[listName], [listName, values]);

  const allSelected = useMemo(() => {
    return listOptions.length === value.length && value.length > 0;
  }, [listOptions, value]);

  /**
   * Cases:
   * 1. CLIENT
   *  - select all list options & all client groups
   * 2. OEM
   *  - select all oems & all oem groups
   *  & all related clients with groups
   */
  function handleSelectAll() {
    if (listName === "oems") {
      const clientIds = getClientTypeIds(
        listGroups,
        listOptions,
        restrictTargets
      );
      setValues({
        ...values,
        oems: listOptions,
        clientTypes: clientIds,
        oemGroups: listGroups,
        clientGroups: listOptions,
      });
    }
    if (listName === "clientTypes") {
      setValues({
        ...values,
        clientTypes: listOptions,
        clientGroups: listGroups,
      });
    }
  }

  /**
   * Cases:
   * 1. CLIENT
   *  - remove all clients & all client groups
   *  & all OEM groups (because clients are empty and platform autofill would include them once again)
   * 2. OEM
   *  - remove all OEMs & all oem groups
   *  & all related clients with groups
   */
  function handleRemoveAll() {
    if (listName === "oems") {
      setValues({
        ...values,
        oems: [],
        clientTypes: [],
        oemGroups: [],
        clientGroups: [],
      });
    }
    if (listName === "clientTypes") {
      setValues({
        ...values,
        clientTypes: [],
        oemGroups: [],
        clientGroups: [],
      });
    }
  }

  function renderStatus() {
    return listOptions.length === value.length
      ? TEXTS.TARGET_ALL
      : value.length === 0
        ? TEXTS.TARGET_NONE
        : `${value.length} of ${listOptions.length}`;
  }

  return (
    <div className={styles.searchSummary} data-test-id={dataTestId}>
      <Checkbox
        name={`all-devices-${listName}`}
        checked={allSelected}
        className={styles.toggleInput}
        disabled={listOptions.length === 0 || platformTypes.length === 0}
        label={TEXTS.TOGGLE_ALL}
        value="ignore"
        dataTestId={`${dataTestId}-toggle-all-checkbox`}
        onChange={() => (allSelected ? handleRemoveAll() : handleSelectAll())}
      />
      <p className={styles.targetCount}>
        <em>{TEXTS.TARGET_HEADER}</em>
        <br />
        {renderStatus()}
      </p>
    </div>
  );
}
